import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePreview } from '../contexts/PreviewContext';
import { getUploadUrl, adminAPI } from '../services/api';
import FeedbackModal from './FeedbackModal';

const DEFAULT_LOGO = '/logo-white.png';

const Navbar = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { theme } = useTheme();
  const { previewDomain, previewData, enterPreview, exitPreview, canPreview, isPreviewMode } = usePreview();

  const [availableBrands, setAvailableBrands] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);

  // Fetch brands for preview dropdown
  useEffect(() => {
    if (canPreview) {
      fetchBrands();
    }
  }, [canPreview]);

  const fetchBrands = async () => {
    try {
      const data = await adminAPI.getBrandings();
      const brands = data.filter(b => b.domain && b.domain !== 'drummond.com');
      setAvailableBrands(brands);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  // Use preview branding if in preview mode, otherwise use regular theme
  const displayBranding = isPreviewMode && previewData?.branding
    ? previewData.branding
    : theme;

  // Determine if this is a branded client (non-Drummond user with custom branding)
  const isBrandedClient = user && !user.is_drummond_user && theme.company_name && theme.company_name !== 'Drummond Portal';

  // Use client logo if available (with full URL), otherwise default Drummond logo
  const logoUrl = displayBranding.logo_url ? getUploadUrl(displayBranding.logo_url) : DEFAULT_LOGO;
  const logoHeight = displayBranding.logo_height ?? 40;

  return (
    <nav className="navbar navbar-expand-lg navbar-dark glass-nav mb-3">
      <div className="container">
        <Link className="navbar-brand d-flex align-items-center text-white" to="/dashboard" style={{ cursor: 'pointer', textDecoration: 'none' }}>
          <i className="bi bi-house-door-fill me-2" style={{ fontSize: '1.3rem' }}></i>
          <img
            src={logoUrl}
            alt="Logo"
            className="navbar-logo me-3"
            style={{
              height: `${logoHeight}px`,
              width: 'auto',
              objectFit: 'contain'
            }}
          />
          <span className="fw-bold">
            DRUMMOND PORTAL
            {isPreviewMode && previewData && (
              <span className="client-name">
                <span className="mx-2 opacity-75">|</span>
                {previewData.branding?.company_name || previewDomain}
                <span
                  className="badge bg-warning text-dark ms-2"
                  style={{ fontSize: '0.7rem' }}
                >
                  PREVIEW MODE
                </span>
              </span>
            )}
            {!isPreviewMode && isBrandedClient && (
              <span className="client-name">
                <span className="mx-2 opacity-75">|</span>
                {theme.company_name}
              </span>
            )}
          </span>
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav ms-auto">
            {isAuthenticated && user ? (
              <>
                {/* Preview Dropdown - Only for Drummond Users */}
                {canPreview && (
                  <li className="nav-item me-3 d-flex align-items-center">
                    <select
                      className="nav-select"
                      id="previewDomain"
                      name="previewDomain"
                      value={previewDomain || ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          enterPreview(e.target.value);
                        } else {
                          exitPreview();
                        }
                      }}
                    >
                      <option value="">Admin View</option>
                      <optgroup label="Preview as Client:">
                        {availableBrands.map(brand => (
                          <option key={brand.domain} value={brand.domain}>
                            {brand.company_name || brand.domain}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </li>
                )}

                <li className="nav-item d-flex align-items-center">
                  <span className="nav-link nav-user">
                    {user.full_name || user.email}
                  </span>
                </li>
                <li className="nav-item d-flex align-items-center">
                    <Link className="nav-btn ms-2" to="/faq">
                      FAQ
                    </Link>
                  </li>
                <li className="nav-item d-flex align-items-center">
                  <button className="nav-btn ms-2" onClick={() => setShowFeedback(true)}>
                    Feedback
                  </button>
                </li>
                {user.is_drummond_user && !isPreviewMode && (
                  <li className="nav-item d-flex align-items-center">
                    <Link className="nav-btn ms-2" to="/admin">
                      Admin
                    </Link>
                  </li>
                )}
                <li className="nav-item d-flex align-items-center">
                  <button
                    className="nav-btn ms-2"
                    onClick={logout}
                  >
                    Logout
                  </button>
                </li>
              </>
            ) : null}
          </ul>
        </div>
      </div>
      <FeedbackModal show={showFeedback} onClose={() => setShowFeedback(false)} user={user} />
    </nav>
  );
};

export default Navbar;
